import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';

const ProfileReminder: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        // Only show reminder if user is authenticated and hasn't filled profile
        // and we haven't dismissed it in this browser session
        if (!user) {
            setShowModal(false);
            return;
        }

        // Check completeness directly or via user object
        // For security/accuracy, we check if key professional fields are missing
        const professional = user.professional || {};
        const isProfileEmpty = !professional.title || !professional.availability || professional.availability === 'Unavailable';
        const completeness = user.profile?.completeness || 0;

        if (completeness < 100 || isProfileEmpty) {
            const hasDismissed = sessionStorage.getItem('profile_completeness_reminder_dismissed');
            
            // Don't show if they are already on the settings page or profile completion page
            const currentPath = location.pathname.toLowerCase();
            const isSettings = currentPath === '/settings' || currentPath.includes('settings') || currentPath === '/profile-completion';

            if (!hasDismissed && !isSettings) {
                // Delay slightly for dramatic effect (premium feel)
                const timer = setTimeout(() => setShowModal(true), 2500);
                return () => clearTimeout(timer);
            } else if (isSettings) {
                setShowModal(false);
            }
        } else {
            setShowModal(false);
        }
    }, [user, location.pathname]);

    if (!showModal) return null;

    return (
        <AnimatePresence>
            {showModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => {
                            sessionStorage.setItem('profile_completeness_reminder_dismissed', 'true');
                            setShowModal(false);
                        }}
                        className="absolute inset-0 bg-black/85 backdrop-blur-md"
                    />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 30 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 30 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="relative w-full max-w-md bg-premium-wood-dark border border-premium-gold/40 rounded-3xl shadow-[0_30px_70px_rgba(0,0,0,0.9)] overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-premium-gold/15 via-transparent to-transparent pointer-events-none" />
                        
                        <div className="p-8 text-center relative z-10">
                            <div className="w-24 h-24 bg-premium-gold/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-premium-gold/20 shadow-[0_0_30px_rgba(212,175,55,0.15)] group relative">
                                <span className="text-5xl group-hover:scale-110 transition-transform duration-300">🏢</span>
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full border-2 border-premium-wood-dark animate-bounce"></div>
                            </div>
                            
                            <h3 className="text-3xl font-serif font-bold text-premium-gold mb-4 tracking-tight">Setup Your Company Info</h3>
                            <p className="text-white/80 mb-10 max-w-sm mx-auto leading-relaxed text-base">
                                To unlock full access to the project matrix and start earning, you must first complete your professional profile.
                            </p>
                            
                            <div className="space-y-4">
                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        navigate('/settings');
                                    }}
                                    className="w-full py-4.5 bg-gradient-to-r from-premium-gold-dark via-premium-gold to-premium-gold-light text-premium-wood-dark font-black uppercase tracking-wider rounded-2xl shadow-[0_15px_30px_rgba(212,175,55,0.3)] hover:shadow-[0_20px_40px_rgba(212,175,55,0.4)] transition-all transform hover:scale-[1.03] active:scale-[0.97]"
                                >
                                    Complete Profile Now
                                </button>
                                <button
                                    onClick={() => {
                                        sessionStorage.setItem('profile_completeness_reminder_dismissed', 'true');
                                        setShowModal(false);
                                    }}
                                    className="w-full py-3 text-white/40 hover:text-white/80 transition-all text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                                >
                                    Later <span className="text-[10px] opacity-50 font-normal">(Session Dismiss)</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Current Progress bar */}
                        <div className="px-8 pb-8">
                            <div className="h-2 bg-white/5 w-full rounded-full overflow-hidden relative">
                                <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${user?.profile?.completeness || 0}%` }}
                                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
                                    className="h-full bg-gradient-to-r from-premium-gold-dark to-premium-gold-light shadow-[0_0_15px_rgba(212,175,55,0.6)]"
                                />
                            </div>
                            <div className="flex justify-between mt-2 px-1">
                                <span className="text-[10px] text-premium-gold/60 font-black uppercase tracking-widest">Progress Score</span>
                                <span className="text-[10px] text-white/70 font-black tracking-widest">{user?.profile?.completeness || 0}%</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ProfileReminder;
